/**
 * Slice 52 / D2, P2–P4 — choose a room's cover photo from the property's Hospitable photos.
 * One tap picks the photo and saves it; the picker closes. Photos another room already uses show that
 * room's name. Loading, empty and refresh states as designed. Full screen on a phone.
 */
import { useRef } from "react";
import { Check, ImagePlus, Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { lastCheckedText } from "@/lib/roomCoverPhotos";

interface Props {
  roomName: string;
  propertyName: string;
  photos: string[];
  /** Photo URL → name of another room using it (saved or suggested). */
  usedBy: Record<string, string>;
  /** The photo this room would show now (its saved cover or its suggestion). */
  selectedUrl: string | null;
  loading: boolean;
  lastChecked: string | null;
  /** The room is currently marked "No photo needed". */
  noPhotoSelected?: boolean;
  uploading?: boolean;
  onNoPhoto: () => void;
  onUpload: (file: File) => void;
  onPick: (url: string) => void;
  onRefresh: () => void;
  onClose: () => void;
}

export function RoomCoverPicker({
  roomName,
  propertyName,
  photos,
  usedBy,
  selectedUrl,
  loading,
  lastChecked,
  noPhotoSelected = false,
  uploading = false,
  onNoPhoto,
  onUpload,
  onPick,
  onRefresh,
  onClose,
}: Props) {
  const empty = !loading && photos.length === 0;
  const sub = loading
    ? "Getting photos from Hospitable…"
    : empty
      ? "Choose cover photo"
      : `${photos.length} photos from Hospitable`;
  const checked = lastCheckedText(lastChecked);
  const fileRef = useRef<HTMLInputElement>(null);

  // Two ways out when the listing has no photo for this room.
  const otherOptions = (
    <div className="flex flex-wrap gap-2.5 border-b border-[#e6edf1] px-4 py-3 sm:px-[22px]">
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) onUpload(f);
        }}
      />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        className="flex h-11 items-center gap-2 rounded-xl border-[1.5px] border-[#d7e0e6] bg-white px-4 text-[14px] font-bold text-[#0f1720] hover:border-[#24a89a] disabled:opacity-60"
      >
        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" strokeWidth={2.2} />}
        Upload from camera roll
      </button>
      <button
        type="button"
        onClick={onNoPhoto}
        className={cn(
          "flex h-11 items-center gap-2 rounded-xl border-[1.5px] px-4 text-[14px] font-bold",
          noPhotoSelected
            ? "border-[#0f1720] bg-[#0f1720] text-white"
            : "border-[#d7e0e6] bg-white text-[#0f1720] hover:border-[#0f1720]",
        )}
      >
        {noPhotoSelected ? <Check className="h-4 w-4" strokeWidth={3} /> : null}
        No photo for this room
      </button>
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-[70] flex items-start justify-center bg-[rgba(15,23,32,0.42)] sm:pt-[50px]"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex h-full w-full flex-col overflow-hidden bg-white sm:h-auto sm:max-h-[760px] sm:w-[900px] sm:max-w-[94vw] sm:rounded-[18px] sm:shadow-[0_24px_60px_rgba(15,23,32,0.22)]"
      >
        <div className="flex items-center gap-3.5 border-b border-[#e6edf1] px-4 py-3.5 sm:px-[22px] sm:py-[18px]">
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl hover:bg-[#f1f5f7] sm:order-last"
          >
            <X className="h-5 w-5 text-[#0f1720]" strokeWidth={2.2} />
          </button>
          <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
            <div className="text-[19px] font-bold tracking-[-0.3px] text-[#0f1720]">
              <span className="sm:hidden">{roomName}</span>
              <span className="hidden sm:inline">Choose cover photo · {roomName}</span>
            </div>
            <div className="text-[13px] font-medium text-[#64748b]">
              <span className="sm:hidden">{loading || empty ? sub : `Choose cover photo · ${photos.length} from Hospitable`}</span>
              <span className="hidden sm:inline">{sub}</span>
            </div>
          </div>
        </div>

        {otherOptions}

        {loading ? (
          <div className="grid grid-cols-3 gap-2.5 overflow-auto px-4 py-4 sm:grid-cols-6 sm:px-[22px] sm:py-[18px]">
            {Array.from({ length: 18 }).map((_, i) => (
              <div key={i} className="aspect-[4/3] animate-pulse rounded-[10px] bg-[#eef2f5]" />
            ))}
          </div>
        ) : empty ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-16 text-center sm:py-[70px]">
            <div className="text-[22px] font-bold text-[#0f1720]">No photos yet</div>
            <div className="max-w-[420px] text-[15px] font-medium leading-[1.5] text-[#64748b]">
              Hospitable hasn’t sent any photos for {propertyName}. Add photos to the listing there, then refresh.
            </div>
            <button
              type="button"
              onClick={onRefresh}
              className="mt-1 flex h-12 items-center rounded-xl bg-[#24a89a] px-6 text-[14.5px] font-bold text-white hover:bg-[#1b8378]"
            >
              Refresh from Hospitable
            </button>
            {checked ? <div className="text-[13px] font-medium text-[#8494a2]">{checked}</div> : null}
          </div>
        ) : (
          <>
            <div className="min-h-0 flex-1 overflow-auto px-4 py-4 sm:px-[22px] sm:py-[18px]">
              <div className="grid grid-cols-3 gap-2.5 sm:grid-cols-6">
                {photos.map((url) => {
                  const selected = selectedUrl === url;
                  const tag = usedBy[url];
                  return (
                    <button
                      key={url}
                      type="button"
                      onClick={() => onPick(url)}
                      className={cn(
                        "relative aspect-[4/3] overflow-hidden rounded-[10px] bg-[#eef2f5] hover:brightness-95",
                        selected && "outline outline-[3px] outline-offset-2 outline-[#24a89a]",
                      )}
                    >
                      <img src={url} alt="" loading="lazy" className="h-full w-full object-cover" />
                      {tag ? (
                        <span className="absolute bottom-1.5 left-1.5 max-w-[calc(100%-12px)] truncate rounded-full bg-[rgba(15,23,32,0.74)] px-2 py-[3px] text-[11px] font-bold text-white">
                          {tag}
                        </span>
                      ) : null}
                      {selected ? (
                        <span className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-[#24a89a]">
                          <Check className="h-3.5 w-3.5 text-white" strokeWidth={3.2} />
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-[#e6edf1] px-4 py-3 text-[13px] font-medium text-[#64748b] sm:px-[22px]">
              <div className="max-sm:hidden">Tap a photo to use it. It saves right away.</div>
              <button type="button" onClick={onRefresh} className="font-bold text-[#15776d] hover:text-[#1b8378]">
                Missing a photo? Refresh from Hospitable
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
